import { UserType } from '@bv/api/auth';
import { BaseApi, SuccessResponse, type ItemResponse } from '@bv/api/base';
import type { BillingMode, BillingStatus } from '@bv/domain/billing';
import { useUserStore } from '@bv/stores';

/**
 * @interface Service
 * Represents a service (like Call Center or New Chat) available within a workspace.
 */
export interface Service {
  id: string;
  name: string;
  unique_name: ServiceUniqueName;
  start_date: string;
  end_date: string;
}

/**
 * @interface Workspace
 * Defines the structure of a workspace, which contains services.
 */
export interface Workspace {
  id: string;
  name: string;
  acc_number: string;
  is_owner: boolean;
  is_admin: boolean;
  billing_mode: 'bevatel' | 'zoho';
  services: ServiceDetails[];
  integration_limits: {
    developer_api: number;
    webhook: number;
  };
}

/**
 * @interface UserDetails
 * Describes the complete profile of the authenticated user, including their workspaces and roles.
 */
export interface UserDetails {
  id: string;
  username: string;
  email: string;
  img: null | string;
  type: UserType;
  role: {
    id: string;
    name: string;
    permissions: string[];
  };
  recent_workspaces: null | Record<string, string>;
  recent_service: null | string;
  workspaces: Workspace[];
  userStatus?: 0 | 1 | 2; // 0: online, 1: busy, 2: offline
}

/**
 * A constant tuple of all valid service unique names.
 * This is used to derive the `ServiceUniqueName` type.
 */
export const validServiceUniqueNames = ['call-center', 'new-chat', 'chat-business', 'sms', 'cloud-lines'] as const;

/**
 * @typedef {('call-center' | 'new-chat' | 'chat-business' | 'sms' | 'cloud-lines')} ServiceUniqueName
 * A type representing the machine-readable unique name for a service.
 */
export type ServiceUniqueName = (typeof validServiceUniqueNames)[number];

export type { BillingMode, BillingStatus };

/**
 * @interface BillingInfo
 * Contains information about billing for a workspace or service.
 */
export interface BillingInfo {
  billing_mode: BillingMode;
  maximum_users: number | null;
  created_users: number | null;
  status: BillingStatus;
  /**
   * @property {number} remaining - The remaining days of the billing period.
   */
  remaining: number;
  type?: 'migrated' | null;
}

/**
 * @interface AiServiceSettings
 * Shared AI service configuration present on service settings.
 */
export interface AiServiceSettings {
  enabled: boolean;
  provider_type: 'bevatel' | 'hamsa';
  provider_meta?: Record<string, unknown>;
}

/**
 * @interface BaseServiceDetails
 * Defines the common properties shared across all specific service detail types,
 * including billing fields (`billing_mode`, `maximum_users`, …) at the same level as other service fields.
 */
export interface BaseServiceDetails extends BillingInfo {
  id: string;
  name: string;
  unique_name: ServiceUniqueName;
  role: string;
  permissions: string[];
  settings: { ai_service?: AiServiceSettings };
  start_date: string;
  end_date: string;
  can_be_accessed_modules: string[];
}

/**
 * @interface CallCenterDetails
 * Extends BaseServiceDetails with properties specific to the Call Center service.
 */
export interface CallCenterDetails extends BaseServiceDetails {
  unique_name: 'call-center';
  max_extensions: number | null;
  asterisk_server_id: string;
  pbx_name: string;
  settings: {
    pbx_name: string;
    asterisk_server_id: string;
    ai_service: AiServiceSettings;
    dialer_service: {
      enabled: boolean;
      context: string | null;
    };
    call_http_request: string | null;
    recording_duration: {
      type: 'fixed' | 'custom';
      value: number;
    };
    is_voicemail_enabled: boolean;
    is_call_evaluation_enabled: boolean;
    is_report_scheduling_alerts_enabled: boolean;
  };
}

/**
 * @interface CloudLinesDetails
 * Extends BaseServiceDetails with properties specific to the Cloud Lines service.
 */
export interface CloudLinesDetails extends BaseServiceDetails {
  unique_name: 'cloud-lines';
  settings: {
    ai_service: AiServiceSettings;
    can_edit_configurations: boolean;
  };
}

/**
 * @interface ExtensionDetails
 * Contains the configuration details for a user's SIP/PJSIP extension.
 */
export interface ExtensionDetails {
  id: string;
  number: string;
  name: string;
  secret: string;
  is_web_rtc_mode: boolean;
  current_mode: 'webPhone' | 'softPhone';
  driver: 'sip' | 'pjsip';
  can_switch_to_web_rtc: boolean;
  asterisk_server_pbx: string;
}

/**
 * @interface NewBusinessChatService
 * Extends BaseServiceDetails with properties specific to the new business chat service.
 */
export interface NewBusinessChatService extends BaseServiceDetails {
  unique_name: 'new-chat';
  settings: {
    ai_service: AiServiceSettings;
  };
}

/**
 * @interface OtherServiceDetails
 * Fallback for services without a dedicated payload sample (sms, chat-business).
 */
interface OtherServiceDetails extends BaseServiceDetails {
  unique_name: 'sms' | 'chat-business';
}

/**
 * @typedef {(CallCenterDetails | NewBusinessChatService | CloudLinesDetails | OtherServiceDetails)} ServiceDetails
 * A union type that can represent the details of any available service.
 */
export type ServiceDetails = CallCenterDetails | NewBusinessChatService | CloudLinesDetails | OtherServiceDetails;

/**
 * Manages API requests related to user data, workspaces, and services.
 */
export class UserApi extends BaseApi {
  /**
   * Initializes a new instance of the UserApi.
   * Sets the base endpoint to 'iam'.
   */
  public constructor() {
    super('iam');
  }

  /**
   * Fetches the detailed profile of the currently authenticated user.
   * @returns {Promise<ItemResponse<UserDetails>>} A promise that resolves with the user's details.
   */
  async details(): Promise<ItemResponse<UserDetails>> {
    return this.request<undefined, ItemResponse<UserDetails>>({ method: 'get', url: 'user-details' });
  }

  /**
   * Fetches the detailed configuration for a specific service within a workspace.
   * @param {string} [workspaceId] - The ID of the workspace.
   * @param {string} [serviceId] - The ID of the service.
   * @returns {Promise<ItemResponse<ServiceDetails>>} A promise that resolves with the service details.
   */
  async workspaceService(workspaceId?: string, serviceId?: string): Promise<ItemResponse<ServiceDetails>> {
    return this.request<undefined, ItemResponse<ServiceDetails>>({
      method: 'get',
      url: 'user-workspace-service-details',
      headers: { 'x-workspace-id': workspaceId, 'x-service-id': serviceId },
    });
  }

  /**
   * Sets the user's most recently used workspace and service.
   * @param {string} [workspaceId] - The ID of the workspace to switch to.
   * @param {string} [serviceId] - The ID of the service to switch to.
   * @returns {Promise<SuccessResponse>} A promise that resolves on successful completion.
   */
  async switchWorkspaceService(workspaceId?: string, serviceId?: string): Promise<SuccessResponse> {
    return this.request<undefined, SuccessResponse>({
      method: 'post',
      url: 'users/recent/workspace/service',
      headers: { 'x-workspace-id': workspaceId, 'x-service-id': serviceId },
      data: { recent_workspace: workspaceId, recent_service: serviceId },
    });
  }

  /**
   * Retrieves the SIP/PJSIP extension details for the current user.
   * @returns {Promise<ItemResponse<ExtensionDetails>>} A promise that resolves with the extension details.
   */
  async getExtensionDetails(): Promise<ItemResponse<ExtensionDetails>> {
    return this.request<undefined, ItemResponse<ExtensionDetails>>({
      method: 'get',
      url: `users/${useUserStore().userDetails?.id}`,
      absoluteURL: 'freepbx-setting/extensions',
      headers: { 'x-service-id': useUserStore().callCenterService?.id },
    });
  }
}

/**
 * A singleton instance of the UserApi for convenient access throughout the application.
 */
export const userApi: UserApi = new UserApi();
